import { Ajax } from "../../Common/Ajax";
import { GlobalData } from "../../Common/GlobalData";
import { AutoCompleteHelper, Notification, TemplateHelpers } from "../../Common/KendoHelpers";

export class PivotPointPatterns {
    private readonly GRID = '#grid';
    private readonly SPINNER = '#spinner';
    private readonly PATTERN_DATA_SOURCE = new kendo.data.DataSource({
        data: [{
            pattern: "None",
            lengthDepthRequired: false,
        }, {
            pattern: "3 Wk TA",
            lengthDepthRequired: false,
        }, {
            pattern: "4 Wk TA",
            lengthDepthRequired: false,
        }, {
            pattern: "50-Day Break",
            lengthDepthRequired: false,
        }, {
            pattern: "Asecending Base",
            lengthDepthRequired: true,
        }, {
            pattern: "Consolidation",
            lengthDepthRequired: true,
        }, {
            pattern: "Cup",
            lengthDepthRequired: true,
        }, {
            pattern: "Cup with Handle",
            lengthDepthRequired: true,
        }, {
            pattern: "Double Bottom",
            lengthDepthRequired: true,
        }, {
            pattern: "Flat Base",
            lengthDepthRequired: true,
        }, {
            pattern: "IPO Base",
            lengthDepthRequired: false,
        }, {
            pattern: "Obsolete",
            lengthDepthRequired: false,
        }, {
            pattern: "Saucer",
            lengthDepthRequired: true,
        }, {
            pattern: "Saucer with Handle",
            lengthDepthRequired: true,
        }],
    });
    private readonly STAGE_A_DATA_SOURCE = new kendo.data.DataSource({
        data: [{
            stageA: "",
        }, {
            stageA: "1",
        }, {
            stageA: "2",
        }, {
            stageA: "3",
        }, {
            stageA: "4",
        }, {
            stageA: "5",
        }, {
            stageA: "6",
        }, {
            stageA: "7",
        }, {
            stageA: "1b",
        }, {
            stageA: "1c",
        }, {
            stageA: "1d",
        }, {
            stageA: "1e",
        }, {
            stageA: "1f",
        }, {
            stageA: "1g",
        }, {
            stageA: "1h",
        }, {
            stageA: "1i",
        }, {
            stageA: "1j",
        }, {
            stageA: "2b",
        }, {
            stageA: "2c",
        }, {
            stageA: "2d",
        }, {
            stageA: "2e",
        }, {
            stageA: "2f",
        }, {
            stageA: "2g",
        }, {
            stageA: "2h",
        }, {
            stageA: "2i",
        }, {
            stageA: "2j",
        }, {
            stageA: "3b",
        }, {
            stageA: "3c",
        }, {
            stageA: "3d",
        }, {
            stageA: "3e",
        }, {
            stageA: "4b",
        }, {
            stageA: "4c",
        }, {
            stageA: "4d",
        }, {
            stageA: "5b",
        }, {
            stageA: "5c",
        }, {
            stageA: "5d",
        }, {
            stageA: "5e",
        }, {
            stageA: "5f",
        }, {
            stageA: "6b",
        }],
    });

    private readonly columnsHiddenOnInit: string[] = [
        'pattern', 'stageA', 'stageB', 'length', 'depth', 'handle', 'pricePct',
        'volPct', 'pivotDate', 'breakoutDate'
    ];

    private validating = false;
    private setSortOrderButton = $('#setSortOrderButton');
    private validationErrorMessage = $('#validationErrorMessage');
    private showAllColumnsCheckbox = $('#showAllColumns') as JQuery<HTMLInputElement>;
    private showAllColumns = false;
    private grid!: kendo.ui.Grid;
    private gridDataSource: any;

    public constructor() {
        this.init();
    }

    private init() {
        this.defineEvents();

        this.gridDataSource = {
            transport: {
                read: GlobalData.URL_BASE +
                    GlobalData.URL_SUFFIX_IBD_MARKET_SMITH_PIVOT_POINT_PATTERNS,
                update: {
                    url: GlobalData.URL_BASE + GlobalData.URL_SUFFIX_IBD_MARKET_SMITH_PIVOT_POINT_PATTERNS,
                    type: 'PUT',
                    contentType: 'application/json',
                    dataType: 'json',
                },

                parameterMap: function (options: any, operation: any) {
                    if (operation !== "read" && options.models) {
                        return kendo.stringify(options.models);
                    }
                }
            },
            batch: true,
            serverPaging: false,
            serverSorting: false,
            schema: {
                model: {
                    id: "symbol",
                    fields: {
                        symbol: { type: "string", editable: false, nullable: false },
                        companyName: { type: "string", editable: false },
                        pattern: {
                            type: "string",
                            validation: {
                                custom: function (input: any) {
                                    // Workaround to deal with the fact that the autocomplete
                                    // sets the value to "[Object object]" after the first edit.
                                    const grid = $('#grid').data("kendoGrid")!;
                                    const row = input.closest("tr");
                                    const dataItem = grid.dataItem(row) as any;
                                    dataItem.pattern = input["0"].value;

                                    return true;
                                }
                            }
                        },
                        stageA: {
                            type: "string",
                            validation: {
                                custom: (input: any) => {
                                    if (!this.showAllColumns) {
                                        return true;
                                    }

                                    const value = input["0"].value;
                                    if (!value || value == "") {
                                        return true;
                                    }

                                    const valid = ((value.length == 1 || value.length == 2) && value.match(/\d[b-z]*/));
                                    if (valid) {
                                        this.validationErrorMessage.html('');
                                        this.validationErrorMessage.css('display', 'none');

                                        // Workaround to deal with the fact that the autocomplete
                                        // sets the value to "[Object object]" after the first edit.
                                        const grid = $('#grid').data("kendoGrid")!;
                                        const row = input.closest("tr");
                                        const dataItem = grid.dataItem(row) as any;
                                        dataItem.stageA = value;

                                    } else {
                                        this.validationErrorMessage.html('"' + value + '" is not a valid value for StageA');
                                        this.validationErrorMessage.css('display', 'block');
                                    }
                                    return valid;
                                }
                            }
                        },
                        stageB: {
                            type: "string",
                            validation: {
                                custom: (input: any) => {
                                    if (!this.showAllColumns) {
                                        return true;
                                    }

                                    const value = input["0"].value;
                                    const valid = (!value || value == "" || value > 0);
                                    if (valid) {
                                        this.validationErrorMessage.html('');
                                        this.validationErrorMessage.css('display', 'none');
                                    } else {
                                        this.validationErrorMessage.html('StageB must be a positive number');
                                        this.validationErrorMessage.css('display', 'block');
                                    }
                                    return valid;
                                }
                            }
                        },
                        pivotPoint: {
                            type: "number",
                            validation: {
                                custom: (input: any) => {
                                    const value = input["0"].value.replace(/,/g, '');
                                    const valid = !value || value >= 0;
                                    if (valid) {
                                        this.validationErrorMessage.html('');
                                        this.validationErrorMessage.css('display', 'none');
                                    } else {
                                        this.validationErrorMessage.html('Pivot Point cannot be empty or negative');
                                        this.validationErrorMessage.css('display', 'block');
                                    }
                                    return valid;
                                }
                            }
                        },
                        length: {
                            type: "number",
                            validation: {
                                custom: (input: any) => {
                                    if (!this.showAllColumns) {
                                        return true;
                                    }

                                    const value = input["0"].value;
                                    const valid = (!value || value == "" || value > 0);
                                    if (valid) {
                                        this.validationErrorMessage.html('');
                                        this.validationErrorMessage.css('display', 'none');
                                    } else {
                                        this.validationErrorMessage.html('Length must be a positive number');
                                        this.validationErrorMessage.css('display', 'block');
                                    }
                                    return valid;
                                }
                            }
                        },
                        depth: {
                            type: "number",
                            validation: {
                                custom: (input: any) => {
                                    if (!this.showAllColumns) {
                                        return true;
                                    }

                                    // See comment in this var's definition.
                                    if (this.validating) {
                                        this.validating = false;
                                        return true;
                                    }

                                    const value = input["0"].value;
                                    const valid = (!value || value == "" || value > 0);
                                    if (valid) {
                                        if (value && value != "") {
                                            this.validating = true;
                                            const grid = $('#grid').data("kendoGrid")!;
                                            const row = input.closest("tr");
                                            const dataItem = grid.dataItem(row) as any;
                                            dataItem.depth = value / 100;
                                        }
                                        this.validationErrorMessage.html('');
                                        this.validationErrorMessage.css('display', 'none');
                                    } else {
                                        this.validationErrorMessage.html('Depth must be a positive number');
                                        this.validationErrorMessage.css('display', 'block');
                                    }
                                    return valid;
                                }
                            }
                        },
                        handle: {
                            type: "number",
                            validation: {
                                custom: (input: any) => {
                                    if (!this.showAllColumns) {
                                        return true;
                                    }

                                    // See comment in this var's definition.
                                    if (this.validating) {
                                        this.validating = false;
                                        return true;
                                    }

                                    const value = input["0"].value;
                                    const valid = (!value || value == "" || value > 0);
                                    if (valid) {
                                        if (value && value != "") {
                                            this.validating = true;
                                            const grid = $('#grid').data("kendoGrid")!;
                                            const row = input.closest("tr");
                                            const dataItem = grid.dataItem(row) as any;
                                            dataItem.handle = value / 100;
                                        }
                                        this.validationErrorMessage.html('');
                                        this.validationErrorMessage.css('display', 'none');
                                    } else {
                                        this.validationErrorMessage.html('Handle must be a positive number');
                                        this.validationErrorMessage.css('display', 'block');
                                    }
                                    return valid;
                                }
                            }
                        },
                        pricePct: {
                            type: "number",
                            validation: {
                                custom: (input: any) => {
                                    if (!this.showAllColumns) {
                                        return true;
                                    }

                                    // See comment in this var's definition.
                                    if (this.validating) {
                                        this.validating = false;
                                        return true;
                                    }

                                    const value = input["0"].value;
                                    if (value && value != "") {
                                        this.validating = true;
                                        const grid = $('#grid').data("kendoGrid")!;
                                        const row = input.closest("tr");
                                        const dataItem = grid.dataItem(row) as any;
                                        dataItem.pricePct = value / 100;
                                        if (isNaN(dataItem.pricePct)) {
                                            dataItem.pricePct = 0;
                                        }
                                    }
                                    return true;
                                }
                            }
                        },
                        volPct: {
                            type: "number",
                            validation: {
                                custom: (input: any) => {
                                    if (!this.showAllColumns) {
                                        return true;
                                    }

                                    // See comment in this var's definition.
                                    if (this.validating) {
                                        this.validating = false;
                                        return true;
                                    }

                                    const value = input["0"].value;
                                    if (value && value != "") {
                                        this.validating = true;
                                        const grid = $('#grid').data("kendoGrid")!;
                                        const row = input.closest("tr");
                                        const dataItem = grid.dataItem(row) as any;
                                        dataItem.volPct = value / 100;
                                        if (isNaN(dataItem.volPct)) {
                                            dataItem.volPct = 0;
                                        }
                                    }
                                    return true;
                                }
                            }
                        },
                        pivotDate: { type: "date" },
                        breakoutDate: { type: "date" }
                    }
                }
            }
        };

        this.grid = $(this.GRID).kendoGrid({
            dataSource: this.gridDataSource,
            height: 915,
            width: 600,
            editable: true,
            navigatable: true,
            sortable: true,
            filterable: false,
            pageable: false,
            resizable: true,
            toolbar: [
                { name: "save" },
                { name: "cancel" },
            ],
            saveChanges: (e: any) => {
                // Only validate columns if they're all shown.
                if (this.showAllColumns) {
                    this.validateBeforeSave(e);
                }
            },
            columns: [
                {
                    field: 'symbol',
                    title: 'Symbol',
                    width: '6%',
                    sortable: true,
                    headerAttributes: { "class": "table-header-cell", style: "text-align: center; font-size: 14px; font-weight: bold; color: navy" },
                    attributes: { "class": "table-cell", style: "text-align: center; font-size: 14px" },
                },
                {
                    field: 'companyName',
                    title: 'Company Name',
                    width: '24%',
                    sortable: true,
                    headerAttributes: { "class": "table-header-cell", style: "text-align: center; font-size: 14px; font-weight: bold; color: navy" },
                    attributes: { "class": "table-cell", style: "text-align: left; font-size: 14px" },
                },
                {
                    field: "pattern",
                    title: "Pattern",
                    width: "12%",
                    hidden: this.columnsHiddenOnInit.indexOf("pattern") != -1,
                    sortable: true,
                    headerAttributes: { "class": "table-header-cell", style: "text-align: center; font-size: 13px; font-weight: bold; color: navy" },
                    attributes: { "class": "table-cell", style: "text-align: center; font-size: 12px" },
                    template: kendo.template(TemplateHelpers.getPivotPointPatternTemplate('pattern', 'needsVerifying')),
                    editor: (container: JQuery, options: kendo.ui.GridColumnEditorOptions) => {
                        this.patternEditor(container, options);
                    },
                },
                {
                    field: "stageA",
                    title: "Stage<br />A",
                    width: "8%",
                    hidden: this.columnsHiddenOnInit.indexOf("stageA") != -1,
                    sortable: true,
                    headerAttributes: { "class": "table-header-cell", style: "text-align: center; font-size: 12px; font-weight: bold; color: navy" },
                    attributes: { "class": "table-cell", style: "text-align: center; font-size: 12px" },
                    editor: (container: JQuery, options: kendo.ui.GridColumnEditorOptions) => {
                        this.stageAEditor(container, options);
                    },
                },
                {
                    field: "stageB",
                    title: "Stage<br />B",
                    width: "7%",
                    hidden: this.columnsHiddenOnInit.indexOf("stageB") != -1,
                    sortable: false,
                    headerAttributes: { "class": "table-header-cell", style: "text-align: center; font-size: 12px; font-weight: bold; color: navy" },
                    attributes: { "class": "table-cell", style: "text-align: center; font-size: 12px; font-weight: normal;" },
                    editor: (container: JQuery, options: kendo.ui.GridColumnEditorOptions) => {
                        this.intEditor(container, options);
                    },
                },
                {
                    field: "pivotPoint",
                    title: "Pivot<br />Point",
                    width: "8%",
                    hidden: this.columnsHiddenOnInit.indexOf("pivotPoint") != -1,
                    sortable: false,
                    headerAttributes: { "class": "table-header-cell", style: "text-align: center; font-size: 14px; font-weight: bold; color: navy" },
                    attributes: { "class": "table-cell", style: "text-align: center; font-size: 14px; font-weight: normal" },
                    format: "{0:c}",
                    editor: (container: JQuery, options: kendo.ui.GridColumnEditorOptions) => {
                        this.pivotPointEditor(container, options);
                    },
                },
                {
                    field: "length",
                    title: "Length<br />Days",
                    width: "7%",
                    hidden: this.columnsHiddenOnInit.indexOf("length") != -1,
                    sortable: false,
                    headerAttributes: { "class": "table-header-cell", style: "text-align: center; font-size: 12px; font-weight: bold; color: navy" },
                    attributes: { "class": "table-cell", style: "text-align: center; font-size: 12px; font-weight: normal" },
                    editor: (container: JQuery, options: kendo.ui.GridColumnEditorOptions) => {
                        this.intEditor(container, options);
                    },
                },
                {
                    field: "depth",
                    title: "Depth<br />Pct",
                    width: "7%",
                    hidden: this.columnsHiddenOnInit.indexOf("depth") != -1,
                    sortable: false,
                    headerAttributes: { "class": "table-header-cell", style: "text-align: center; font-size: 12px; font-weight: bold; color: navy" },
                    attributes: { "class": "table-cell", style: "text-align: center; font-size: 12px; font-weight: normal" },
                    format: "{0:p0}",
                    editor: (container: JQuery, options: kendo.ui.GridColumnEditorOptions) => {
                        this.intPercentEditor(container, options);
                    },
                },
                {
                    field: "handle",
                    title: "Handle<br />Pct",
                    width: "7%",
                    hidden: this.columnsHiddenOnInit.indexOf("handle") != -1,
                    sortable: false,
                    headerAttributes: { "class": "table-header-cell", style: "text-align: center; font-size: 12px; font-weight: bold; color: navy" },
                    attributes: { "class": "table-cell", style: "text-align: center; font-size: 13px; font-weight: normal" },
                    format: "{0:p0}",
                    editor: (container: JQuery, options: kendo.ui.GridColumnEditorOptions) => {
                        this.intPercentEditor(container, options);
                    },
                },
                {
                    field: "pricePct",
                    title: "Price<br/>Pct",
                    width: "7%",
                    hidden: this.columnsHiddenOnInit.indexOf("pricePct") != -1,
                    sortable: false,
                    headerAttributes: { "class": "table-header-cell", style: "text-align: center; font-size: 12px; font-weight: bold; color: navy" },
                    attributes: { "class": "table-cell", style: "text-align: center; font-size: 12px; font-weight: normal" },
                    format: "{0:p1}",
                    editor: (container: JQuery, options: kendo.ui.GridColumnEditorOptions) => {
                        this.decimalPercentEditor(container, options);
                    },
                },
                {
                    field: "volPct",
                    title: "Vol<br />Pct",
                    width: "7%",
                    hidden: this.columnsHiddenOnInit.indexOf("volPct") != -1,
                    sortable: false,
                    headerAttributes: { "class": "table-header-cell", style: "text-align: center; font-size: 12px; font-weight: bold; color: navy" },
                    attributes: { "class": "table-cell", style: "text-align: center; font-size: 12px; font-weight: normal" },
                    format: "{0:p0}",
                    editor: (container: JQuery, options: kendo.ui.GridColumnEditorOptions) => {
                        this.decimalPercentEditor(container, options);
                    },
                },
                {
                    field: "pivotDate",
                    title: "Pivot<br />Date",
                    width: "10%",
                    hidden: this.columnsHiddenOnInit.indexOf("pivotDate") != -1,
                    sortable: true,
                    headerAttributes: { "class": "table-header-cell", style: "text-align: center; font-size: 12px; font-weight: bold; color: navy" },
                    attributes: { "class": "table-cell", style: "text-align: center; font-size: 12px; font-weight: normal" },
                    template: kendo.template(TemplateHelpers.getFormattedDateTemplate('pivotDate')),
                    editor: (container: JQuery, options: kendo.ui.GridColumnEditorOptions) => {
                        this.dateEditor(container, options);
                    },
                },
                {
                    field: "breakoutDate",
                    title: "Breakout<br />Date",
                    width: "10%",
                    hidden: this.columnsHiddenOnInit.indexOf("breakoutDate") != -1,
                    sortable: true,
                    headerAttributes: { "class": "table-header-cell", style: "text-align: center; font-size: 12px; font-weight: bold; color: navy" },
                    attributes: { "class": "table-cell", style: "text-align: center; font-size: 12px; font-weight: normal" },
                    template: kendo.template(TemplateHelpers.getFormattedDateTemplate('breakoutDate')),
                    editor: (container: JQuery, options: kendo.ui.GridColumnEditorOptions) => {
                        this.dateEditor(container, options);
                    },
                },
            ]
        }).data('kendoGrid')!;
    }

    private defineEvents() {
        // Set the sort order when the user clicks the corresponding button.
        this.setSortOrderButton.off('click').on('click', () => {
            kendo.confirm('Are you sure that you want to set the sort order?')
                .then(() => {
                    this.setSortOrder();
                });
        });

        this.showAllColumnsCheckbox.off('change').on('change', (e: JQuery.ChangeEvent) => {
            this.showAllColumns = e.currentTarget.checked;
            this.showOrHideColumns();
        });
    }

    private setSortOrder() {
        const message = new Notification();
        $(this.SPINNER).show();

        const success = () => {
            message.success("Finished");
        };

        const error = () => {
            message.error('Error');
        };

        const complete = () => {
            $(this.SPINNER).hide();
        };

        Ajax.invoke(GlobalData.URL_SUFFIX_IBD_MARKET_SMITH_PIVOT_POINT_PATTERNS +
            '/SetSortOrder', 'PUT', null, success, error, complete);
    }

    private showOrHideColumns() {
        for (let i = 0; i < this.grid.columns.length; ++i) {
            const column = this.grid.columns[i];
            if (this.showAllColumns) {
                this.grid.showColumn(column.field);
            }
            else if (column?.field && this.columnsHiddenOnInit.indexOf(column.field) != -1) {
                    this.grid.hideColumn(column.field);
            }
        }
    }

    private pivotPointEditor(container: JQuery, options: kendo.ui.GridColumnEditorOptions) {
        const editor = AutoCompleteHelper.getEditor(container, options);
        editor.kendoNumericTextBox({
            decimals: 2,
            spinners: false // This removes the up/down arrows
        });
        AutoCompleteHelper.selectText(editor);
    }

    private patternEditor(container: JQuery, options: kendo.ui.GridColumnEditorOptions) {
        const editor = AutoCompleteHelper.getEditor(container, options);
        editor.kendoAutoComplete({
            dataSource: this.PATTERN_DATA_SOURCE,
            dataTextField: "pattern",
            minLength: 1,
            highlightFirst: true,
            suggest: true,
        });
        AutoCompleteHelper.selectText(editor);
    }

    private stageAEditor(container: JQuery, options: kendo.ui.GridColumnEditorOptions) {
        const editor = AutoCompleteHelper.getEditor(container, options);
        editor.kendoAutoComplete({
            dataSource: this.STAGE_A_DATA_SOURCE,
            dataTextField: "stageA",
            highlightFirst: true,
            suggest: true,
        });
        AutoCompleteHelper.selectText(editor);
    }

    private currencyEditor(container: JQuery, options: kendo.ui.GridColumnEditorOptions) {
        const editor = AutoCompleteHelper.getEditor(container, options);
        editor.kendoNumericTextBox({
            decimals: 2,
        });
        AutoCompleteHelper.selectText(editor);
    }

    private intEditor(container: JQuery, options: kendo.ui.GridColumnEditorOptions) {
        const editor = AutoCompleteHelper.getEditor(container, options);
        editor.kendoNumericTextBox({
            decimals: 0,
            format: "n0",
        });
        AutoCompleteHelper.selectText(editor);
    }

    private intPercentEditor(container: JQuery, options: kendo.ui.GridColumnEditorOptions) {
        this.validating = false;
        const editor = AutoCompleteHelper.getEditor(container, options);
        editor.kendoNumericTextBox({
            decimals: 2,
            format: "p0",
        });
        AutoCompleteHelper.selectText(editor, true);
    }

    private decimalPercentEditor(container: JQuery, options: kendo.ui.GridColumnEditorOptions) {
        this.validating = false;
        const editor = AutoCompleteHelper.getEditor(container, options);
        editor.kendoNumericTextBox({
            decimals: 3,
            format: "p1",
        });
        AutoCompleteHelper.selectText(editor, true);
    }

    // Append the current year or the previous year if the user has finished
    // entering the month and date and has then pressed '/' or '-'.
    //
    // TODO: add check for tab key after "M/d" and add a '/' + currentYear, like Excel?
    private dateEditorKeyup(event: JQuery.KeyUpEvent) {
        if (event.key != '/' && event.key != '*' && event.key != '-') {
            return;
        }

        const value = event.currentTarget.value;
        const currentYear = (new Date()).getFullYear();

        // If the value is in the format "M/d/", append the current year.
        if (value.match(/([1-9]|1[012])\/([1-9]|[12][0-9]|3[01])\//)) {
            event.currentTarget.value = value + currentYear;
        }
        // If the value is in the format "M/d*", append the previous year.
        else if (value.match(/([1-9]|1[012])\/([1-9]|[12][0-9]|3[01])\*/)) {
            event.currentTarget.value = value.substring(0, value.length - 1) + '/' +
                (currentYear - 1);
        }
        // If the value is in the format "M/d-", append the previous year.
        else if (value.match(/([1-9]|1[012])\/([1-9]|[12][0-9]|3[01])\-/)) {
            event.currentTarget.value = value.substring(0, value.length - 1) + '/' +
                (currentYear - 2);
        }
    }

    private dateEditor(container: JQuery, options: kendo.ui.GridColumnEditorOptions) {
        this.validating = false;
        const editor = AutoCompleteHelper.getEditor(container, options);
        editor.css('width', '70px');
        editor.off('keyup').on('keyup', (e: JQuery.KeyUpEvent) => {
            this.dateEditorKeyup(e);
        });
        AutoCompleteHelper.selectText(editor, false, true);
    }

    private validateBeforeSave(e: any) {
        const dataSource = ($(this.GRID).data("kendoGrid") as kendo.ui.Grid).dataSource;
        const rows = dataSource.view();
        const invalidRows = [];

        // Check all of the global validation rules.
        for (let i = 0; i < rows.length; ++i) {
            rows[i].validationErrorMessage = '';
            if (rows[i].dirty) {

                // All records must have a PivotDate.
                if (!rows[i].pivotDate) {
                    rows[i].validationErrorMessage += rows[i].symbol + ': must have a PivotDate<br>';
                    invalidRows.push(rows[i]);
                }

                // Records with certain patterns must have Length and Depth.
                if (!rows[i].length || !rows[i].depth) {
                    const patternData = this.PATTERN_DATA_SOURCE.data();
                    for (let j = 0; j < patternData.length; ++j) {
                        if (rows[i].pattern == patternData[j].pattern && patternData[j].lengthDepthRequired) {
                            rows[i].validationErrorMessage += rows[i].symbol + ': must have a Length and Depth<br>';
                            if (!invalidRows.length || invalidRows[invalidRows.length - 1].symbol != rows[i].symbol)
                                invalidRows.push(rows[i]);
                            break;
                        }
                    }
                }

                // Records with BreakoutDate must have PricePct and VolPct.
                if (rows[i].breakoutDate && (!rows[i].pricePct || !rows[i].volPct)) {
                    rows[i].validationErrorMessage += rows[i].symbol + ': a BreakoutDate must have PricePct and VolPct<br>';
                    if (!invalidRows.length || invalidRows[invalidRows.length - 1].symbol != rows[i].symbol)
                        invalidRows.push(rows[i]);
                }

                // Records without BreakoutDate must not have PricePct and VolPct.
                if (!rows[i].breakoutDate && (rows[i].pricePct || rows[i].volPct)) {
                    rows[i].validationErrorMessage += rows[i].symbol + ': PricePct and VolPct should not be used without a BreakoutDate<br>';
                    if (!invalidRows.length || invalidRows[invalidRows.length - 1].symbol != rows[i].symbol)
                        invalidRows.push(rows[i]);
                }

                // Handle patterns must have Handle set.
                if (!rows[i].handle && rows[i].pattern.toLowerCase().indexOf('handle') != -1) {
                    rows[i].validationErrorMessage += rows[i].symbol + ': ' + rows[i].pattern + ' must have HandlePct<br>';
                    if (!invalidRows.length || invalidRows[invalidRows.length - 1].symbol != rows[i].symbol)
                        invalidRows.push(rows[i]);
                }

                // Non-handle patterns must not have Handle set.
                if (rows[i].handle && rows[i].pattern.toLowerCase().indexOf('handle') == -1) {
                    rows[i].validationErrorMessage += rows[i].symbol + ': ' + rows[i].pattern + ' must not have HandlePct<br>';
                    if (!invalidRows.length || invalidRows[invalidRows.length - 1].symbol != rows[i].symbol)
                        invalidRows.push(rows[i]);
                }
            }
        }

        // If there were any invalid rows, prevent the update and display the error messages.
        if (invalidRows.length > 0) {
            // Don't call the server with the update.
            e.preventDefault();

            // Display all of the error messages.
            let globalValidationErrorMessages = '';
            for (let i = 0; i < invalidRows.length; ++i) {
                globalValidationErrorMessages += invalidRows[i].validationErrorMessage;
            }
            $('#globalValidationErrorMessages').html(globalValidationErrorMessages);
        }
        else {
            $('#globalValidationErrorMessages').html('');
        }
    }

    private cancelChanges() {
        this.validationErrorMessage.html('');
        this.validationErrorMessage.css('display', 'none');
    }
}
